import { TIMELINE_RULER_TICK_WIDTH_PX } from "../../../../../config/config";

const TICK_LINE_WIDTH = 1;

const CLASSIC_MAJOR_TICK_VERTICAL_OFFSET = 20;
const CLASSIC_MAJOR_TICK_HEIGHT_PX = 12;

const DOTTED_MAJOR_TICK_VERTICAL_OFFSET = 12;
const DOTTED_MAJOR_TICK_HEIGHT_PX = 6;
const DOTTED_MINOR_DOT_VERTICAL_OFFSET = 15;
const DOTTED_MINOR_DOT_RADIUS_PX = 0.9;
const DOTTED_MINOR_DIVISIONS = 5;

const CLASSIC_TIME_LABEL_VERTICAL_OFFSET = 14;
const DOTTED_TIME_LABEL_VERTICAL_OFFSET = 9;
const DOTTED_LABEL_FONT = '500 9px Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const DOTTED_LABEL_EDGE_GUARD_PX = 20;

const DESIRED_MAJOR_TICK_PX = 100;
const MIN_MAJOR_TICK_PX = 70;
const MAX_MAJOR_TICK_PX = 140;

const MAJOR_TICK_CANDIDATES_SEC = [
    0.001, 0.002, 0.005,
    0.01, 0.02, 0.05,
    0.1, 0.2, 0.25, 0.5,
    1, 2, 5,
    10, 15, 30,
    60, 120, 300, 600, 900, 1800,
    3600, 7200, 14400,
];

function pickMajorTickSec(pxPerSec: number): number {
    if (!Number.isFinite(pxPerSec) || pxPerSec <= 0) return 1;
    let best = MAJOR_TICK_CANDIDATES_SEC[0] ?? 1;
    let bestScore = Number.POSITIVE_INFINITY;

    for (const sec of MAJOR_TICK_CANDIDATES_SEC) {
        const px = sec * pxPerSec;
        const inRange = px >= MIN_MAJOR_TICK_PX && px <= MAX_MAJOR_TICK_PX;
        const score = inRange ? Math.abs(px - DESIRED_MAJOR_TICK_PX) : Math.abs(px - DESIRED_MAJOR_TICK_PX) + 1_000;
        if (score < bestScore) {
            bestScore = score;
            best = sec;
        }
    }

    return best;
}

function decimalsForStep(stepSec: number): number {
    if (!Number.isFinite(stepSec) || stepSec <= 0) return 0;
    for (let dec = 0; dec <= 3; dec++) {
        const scaled = stepSec * Math.pow(10, dec);
        if (Math.abs(scaled - Math.round(scaled)) < 1e-9) return dec;
    }
    return 3;
}

function formatRulerTime(seconds: number, decimals: number): string {
    const sign = seconds < 0 ? "-" : "";
    const abs = Math.abs(seconds);

    const scale = Math.pow(10, Math.max(0, Math.min(3, decimals)));
    const scaledTotalSeconds = Math.round(abs * scale);

    const wholeSeconds = Math.floor(scaledTotalSeconds / scale);
    const fracUnits = scaledTotalSeconds % scale; // 0..scale-1

    const hours = Math.floor(wholeSeconds / 3600);
    const minutes = Math.floor((wholeSeconds % 3600) / 60);
    const secsInt = wholeSeconds % 60;

    const secsBase = secsInt.toString().padStart(2, "0");
    const secStr =
        decimals > 0
            ? `${secsBase}.${fracUnits.toString().padStart(decimals, "0")}`
            : secsBase;

    if (hours > 0) return `${sign}${hours}:${minutes.toString().padStart(2, "0")}:${secStr}`;
    return `${sign}${minutes}:${secStr}`;
}

export const drawCanvasTimelineRuler = (
    context: CanvasRenderingContext2D,
    duration: number,
    options: {
        scrollOffset: number;
        fillColor: string;
        zoom: number;
        leftPadPx?: number;
        leftPadSeconds?: number;
        baseStartSec?: number;
        /** Opt-in presentation used by the Libretto editor. The classic default is shared elsewhere. */
        variant?: "classic" | "dotted";
    },
) => {
    const devicePixelRatio = window.devicePixelRatio || 1;
    // Prefer the CSS box: it remains the authoritative logical size if the window
    // moves between displays before the backing bitmap is resized for the new DPR.
    const height = context.canvas.clientHeight || context.canvas.height / devicePixelRatio;
    const width = context.canvas.clientWidth || context.canvas.width / devicePixelRatio;

    const pxPerSec = TIMELINE_RULER_TICK_WIDTH_PX * options.zoom;
    const majorTickSec = pickMajorTickSec(pxPerSec);
    const majorTickPx = majorTickSec * pxPerSec;
    const labelDecimals = decimalsForStep(majorTickSec);

    const leftPadPx =
        Number.isFinite(options.leftPadPx as number)
            ? Number(options.leftPadPx)
            : (options.leftPadSeconds ?? 0) * pxPerSec;
    const effectiveScroll = options.scrollOffset - leftPadPx;
    const baseStartSec = options.baseStartSec ?? 0;
    const isDottedVariant = options.variant === "dotted";
    const majorTickVerticalOffset = isDottedVariant
        ? DOTTED_MAJOR_TICK_VERTICAL_OFFSET
        : CLASSIC_MAJOR_TICK_VERTICAL_OFFSET;
    const majorTickHeightPx = isDottedVariant
        ? DOTTED_MAJOR_TICK_HEIGHT_PX
        : CLASSIC_MAJOR_TICK_HEIGHT_PX;
    const timeLabelVerticalOffset = isDottedVariant
        ? DOTTED_TIME_LABEL_VERTICAL_OFFSET
        : CLASSIC_TIME_LABEL_VERTICAL_OFFSET;

    initCanvas(context, width, height, {
        fillColor: options.fillColor,
        scrollOffset: effectiveScroll,
        variant: options.variant ?? "classic",
    });

    // Determine visible absolute time range.
    const visibleStartSec = baseStartSec + (effectiveScroll / pxPerSec);
    const visibleEndSec = baseStartSec + ((effectiveScroll + width) / pxPerSec);

    // Major ticks aligned to absolute time (multiples of majorTickSec from 0:00).
    const firstMajorTickSec = Math.floor(visibleStartSec / majorTickSec) * majorTickSec;
    const lastMajorTickSec = Math.ceil(visibleEndSec / majorTickSec) * majorTickSec;

    if (isDottedVariant) {
        drawMinorDots(
            context,
            firstMajorTickSec,
            lastMajorTickSec,
            majorTickSec,
            baseStartSec,
            pxPerSec,
            effectiveScroll,
            width,
        );
    }

    // Major ticks remain aligned to absolute time; only their visual length changes.
    if (isDottedVariant) context.save();
    if (isDottedVariant) context.globalAlpha = 0.68;
    context.beginPath();

    for (let t = firstMajorTickSec; t <= lastMajorTickSec + 1e-9; t += majorTickSec) {
        const xPx = (t - baseStartSec) * pxPerSec;
        context.moveTo(xPx, majorTickVerticalOffset);
        context.lineTo(xPx, majorTickVerticalOffset + majorTickHeightPx);
    }

    context.stroke();
    if (isDottedVariant) context.restore();

    // Draw labels on major ticks
    if (isDottedVariant) context.save();
    if (isDottedVariant) context.globalAlpha = 0.9;
    for (let t = firstMajorTickSec; t <= lastMajorTickSec + 1e-9; t += majorTickSec) {
        const xPx = (t - baseStartSec) * pxPerSec;

        // Only label ticks in the visible canvas area (small overscan)
        if (xPx < effectiveScroll - majorTickPx || xPx > effectiveScroll + width + majorTickPx) continue;

        const formattedTime = formatRulerTime(t, labelDecimals);
        const screenX = xPx - effectiveScroll;

        // Pick an alignment based on the position
        let timeLabelAlign: CanvasTextAlign = "center";
        if (isDottedVariant && screenX <= DOTTED_LABEL_EDGE_GUARD_PX) {
            timeLabelAlign = "left";
        } else if (isDottedVariant && screenX >= width - DOTTED_LABEL_EDGE_GUARD_PX) {
            timeLabelAlign = "right";
        } else if (!isDottedVariant && Math.abs(t) < 1e-9) {
            timeLabelAlign = "left";
        } else if (
            !isDottedVariant &&
            (t > duration || Math.abs(t - duration) < 0.2) &&
            screenX > width - 15.0
        ) {
            timeLabelAlign = "right";
        }

        drawTimeLabel(context, xPx, formattedTime, timeLabelAlign, timeLabelVerticalOffset);
    }
    if (isDottedVariant) context.restore();

    context.restore();
};

const drawMinorDots = (
    context: CanvasRenderingContext2D,
    firstMajorTickSec: number,
    lastMajorTickSec: number,
    majorTickSec: number,
    baseStartSec: number,
    pxPerSec: number,
    effectiveScroll: number,
    width: number,
) => {
    context.save();
    context.globalAlpha = 0.48;
    context.beginPath();

    for (let major = firstMajorTickSec; major <= lastMajorTickSec + 1e-9; major += majorTickSec) {
        for (let division = 1; division < DOTTED_MINOR_DIVISIONS; division += 1) {
            const t = major + (majorTickSec * division) / DOTTED_MINOR_DIVISIONS;
            const xPx = (t - baseStartSec) * pxPerSec;
            const screenX = xPx - effectiveScroll;

            if (screenX < -DOTTED_MINOR_DOT_RADIUS_PX || screenX > width + DOTTED_MINOR_DOT_RADIUS_PX) {
                continue;
            }

            context.moveTo(xPx + DOTTED_MINOR_DOT_RADIUS_PX, DOTTED_MINOR_DOT_VERTICAL_OFFSET);
            context.arc(
                xPx,
                DOTTED_MINOR_DOT_VERTICAL_OFFSET,
                DOTTED_MINOR_DOT_RADIUS_PX,
                0,
                Math.PI * 2,
            );
        }
    }

    context.fill();
    context.restore();
};

const initCanvas = (
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    options: { fillColor: string; scrollOffset: number; variant: "classic" | "dotted" },
) => {
    context.clearRect(0, 0, width, height);
    context.strokeStyle = options.fillColor;
    context.fillStyle = options.fillColor;
    context.lineWidth = TICK_LINE_WIDTH;
    context.textAlign = "center";

    if (options.variant === "dotted") {
        context.font = DOTTED_LABEL_FONT;
        context.textBaseline = "alphabetic";
        context.lineCap = "round";
    }

    context.save();
    context.translate(-options.scrollOffset, 0);
};

const drawTimeLabel = (
    context: CanvasRenderingContext2D,
    x: number,
    time: string,
    align: CanvasTextAlign,
    verticalOffset: number,
) => {
    context.textAlign = align;
    context.fillText(time, x, verticalOffset);
};
